import { RiArrowDownSLine, RiArrowUpSLine, RiCheckLine, RiCloseLine, RiLoader4Line } from '@remixicon/react';
import { cva, type VariantProps } from 'class-variance-authority';
import { ReactNode } from 'react';
import {
  Button,
  ComboBox,
  ComboBoxProps,
  Group,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Text,
} from 'react-aria-components';
import { agButtonVariants } from '../AgButton';
import { cn } from '../utils';
import { useAutocompleteController } from './useAutocompleteController';

const agAutocompleteInputVariants = cva('inline-flex items-center w-full bg-white border transition-all duration-150', {
  variants: {
    variant: { default: 'border-neutral-5 hover:border-neutral-6', error: 'border-red-text' },
    size: {
      sm: 'h-[32px] px-2.5 gap-1.5 text-14',
      md: 'h-[40px] px-3 gap-2 text-14',
      lg: 'h-[48px] px-4 gap-2.5 text-16',
    },
    shape: { rounded: 'rounded-md', pill: 'rounded-full' },
    isFocused: { true: '', false: '' },
    isDisabled: { true: 'opacity-60 pointer-events-none bg-neutral-2', false: '' },
  },
  compoundVariants: [
    { variant: 'default', isFocused: true, className: 'border-brand-6 ring-2 ring-ring-primary' },
    { variant: 'error', isFocused: true, className: 'ring-2 ring-ring-destructive' },
  ],
});

const agLabelVariants = cva('text-neutral-text font-medium', {
  variants: { size: { sm: 'text-13', md: 'text-14', lg: 'text-14' } },
});

type AgAutocompleteVariantProps = VariantProps<typeof agAutocompleteInputVariants>;

export interface AgAutocompleteProps<T extends object>
  extends Omit<ComboBoxProps<T>, 'children' | 'items' | 'value' | 'onChange' | 'defaultValue'> {
  /** Label text displayed above the input */
  label?: string;
  /** Show red asterisk for required fields */
  isRequired?: boolean;
  /** The list of options to display */
  options: T[];
  /** Function to get the display label for an option */
  getOptionLabel: (option: T) => string;
  /** Function to get a unique key for an option */
  getOptionKey?: (option: T) => string | number;
  /** Function to determine if two options are equal */
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Size variant */
  size?: AgAutocompleteVariantProps['size'];
  /** Shape variant */
  shape?: AgAutocompleteVariantProps['shape'];
  /** Additional class for the container */
  className?: string;
  /** Whether the field is in an error state */
  isInvalid?: boolean;
  /** The currently selected value */
  value?: T | null;
  /** Callback when selection changes */
  onChange?: (value: T | null) => void;
  /** Whether to allow clearing the selection */
  isClearable?: boolean;
  /** Content to render before the input (icons, etc.) */
  startContent?: ReactNode;
  /** Content to render after the input (icons, buttons, etc.) */
  endContent?: ReactNode;
  /** Whether data is loading (shows spinner) */
  isLoading?: boolean;
  /** Whether to filter options locally. Set to false for async/external filtering */
  filterOptions?: boolean;
  /** Callback when input value changes. Use for async search */
  onInputChange?: (value: string) => void;
  /** Content to show when no options match. Can be a ReactNode for custom content */
  noOptionsText?: ReactNode;
  /** Custom render function for options */
  renderOption?: (option: T, state: { isSelected: boolean; isFocused: boolean }) => ReactNode;
  /** Suggested value to show when no value is selected (displayed with reduced opacity) */
  suggestedValue?: T;
  /** Helper text displayed below the input */
  helperText?: string;
  /** Focus the text input on mount (e.g. when the field is shown in a modal). */
  autoFocus?: boolean;
  selectOnEnter?: boolean;
  selectOnTab?: boolean;
  /** Content rendered below the options inside the dropdown (same slot as `AgSelect`'s `footer`) */
  footer?: ReactNode;
}

export function AgAutocomplete<T extends object>({
  label,
  isRequired,
  options,
  getOptionLabel,
  getOptionKey,
  isOptionEqualToValue,
  placeholder = 'Select...',
  size = 'md',
  shape = 'rounded',
  className,
  isDisabled,
  isInvalid,
  value,
  onChange,
  isClearable = true,
  startContent,
  endContent,
  isLoading = false,
  filterOptions = true,
  onInputChange: onInputChangeProp,
  noOptionsText = 'No options found',
  renderOption,
  suggestedValue,
  helperText,
  autoFocus,
  selectOnEnter,
  selectOnTab,
  footer,
  ...props
}: AgAutocompleteProps<T>) {
  const controller = useAutocompleteController<T>({
    options,
    getOptionLabel,
    getOptionKey,
    isOptionEqualToValue,
    value,
    onChange,
    isLoading,
    filterOptions,
    onInputChangeProp,
    suggestedValue,
    autoFocus,
    selectOnEnter,
    selectOnTab,
  });

  return (
    <ComboBox
      className={cn('flex flex-col gap-2 w-full', className)}
      isDisabled={isDisabled}
      onSelectionChange={controller.handleSelectionChange}
      inputValue={controller.displayValue}
      onInputChange={controller.handleInputChange}
      onFocusChange={controller.onFocusChange}
      allowsCustomValue
      allowsEmptyCollection
      menuTrigger="focus"
      // When the consumer disables our local filter (filterOptions=false, e.g.
      // async search backed by Algolia), also disable react-aria's built-in
      // text filter so the async results aren't silently hidden.
      defaultFilter={filterOptions ? undefined : () => true}
      {...props}
    >
      {label && (
        <Label className={cn(agLabelVariants({ size }))}>
          {label}
          {isRequired && <span className="text-red-text ml-2">*</span>}
        </Label>
      )}
      <Group
        ref={controller.groupRef}
        className={cn(
          agAutocompleteInputVariants({
            variant: isInvalid ? 'error' : 'default',
            size,
            shape,
            isFocused: controller.isFocused,
            isDisabled,
          })
        )}
      >
        {startContent && <span className="flex items-center shrink-0 text-neutral-alpha-8">{startContent}</span>}
        <Input
          ref={controller.inputRef}
          onKeyDownCapture={controller.handleKeyDownCapture}
          onKeyDown={controller.handleKeyDown}
          placeholder={placeholder}
          className={cn(
            'flex-1 bg-transparent outline-none text-neutral-text placeholder:text-neutral-alpha-7 min-w-0',
            controller.isShowingSuggested && 'opacity-50'
          )}
        />
        <span className="flex items-center gap-1 shrink-0">
          {isLoading && <RiLoader4Line className="h-4 w-4 text-neutral-alpha-7 animate-spin" aria-hidden="true" />}
          {endContent}
          {/* Native <button> instead of AgButton to bypass react-aria's
              event handling — AgButton uses usePress which lets pointer events
              propagate into the ComboBox, causing the popover to flicker open
              on clear. stopPropagation on the native element keeps the
              ComboBox completely unaware of the interaction. */}
          {isClearable && value && !isLoading && (
            <button
              type="button"
              tabIndex={-1}
              aria-label="Clear"
              className={cn(
                agButtonVariants({ variant: 'ghost', size: 'icon-xs', shape: 'circle' }),
                isDisabled && 'invisible'
              )}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
                controller.handleClear();
              }}
            >
              <RiCloseLine className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
          <Button className="p-1 text-neutral-alpha-7">
            {controller.isFocused ? (
              <RiArrowUpSLine className="h-4 w-4" aria-hidden="true" />
            ) : (
              <RiArrowDownSLine className="h-4 w-4" aria-hidden="true" />
            )}
          </Button>
        </span>
      </Group>
      <Popover
        triggerRef={controller.groupRef}
        placement="bottom start"
        offset={8}
        style={{ width: controller.groupWidth, zIndex: 200000 }}
        className={cn(
          'overflow-hidden rounded-md bg-white border border-neutral-5 shadow-lg',
          'entering:animate-in entering:fade-in-0 entering:zoom-in-95',
          'exiting:animate-out exiting:fade-out-0 exiting:zoom-out-95'
        )}
      >
        <ListBox
          id={controller.listboxId}
          className={cn(
            'outline-none max-h-[300px] overflow-auto',
            controller.filteredOptions.length > 0 && 'p-1.5',
            // Show which row Tab would take. react-aria only marks an option `data-focused` once the
            // user arrows onto one, so until then nothing looks chosen even though Tab has a target.
            // Scoped by `:not(:has(...))` so an explicit arrow selection immediately wins.
            selectOnTab &&
              controller.filteredOptions.length > 0 &&
              '[&:not(:has([data-focused]))>*:first-child]:bg-[rgba(10,15,41,0.04)]'
          )}
          renderEmptyState={() =>
            isLoading ? (
              <div className="p-1.5">
                <div className="px-2.5 py-1.5 text-14 text-neutral-alpha-7 flex items-center gap-2">
                  <RiLoader4Line className="h-4 w-4 animate-spin" />
                  Loading...
                </div>
              </div>
            ) : (
              <div className="p-1.5">
                <div className="px-2.5 py-1.5 text-14 text-neutral-alpha-7">{noOptionsText}</div>
              </div>
            )
          }
        >
          {controller.filteredOptions.map((option) => (
            <ListBoxItem
              key={controller.getKey(option)}
              id={controller.getKey(option)}
              textValue={getOptionLabel(option)}
              className={cn(
                'relative flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md cursor-pointer outline-none text-14 text-neutral-text',
                'hover:bg-[rgba(10,15,41,0.04)] data-focused:bg-[rgba(10,15,41,0.04)]',
                controller.isOptionSelected(option) && 'bg-brand-alpha-1',
                'data-disabled:opacity-50 data-disabled:pointer-events-none'
              )}
            >
              {({ isFocused: isItemFocused }) => {
                const selected = controller.isOptionSelected(option);
                return renderOption ? (
                  renderOption(option, { isSelected: selected, isFocused: isItemFocused })
                ) : (
                  <>
                    <span className="flex-1 truncate">{getOptionLabel(option)}</span>
                    {selected && <RiCheckLine className="h-4 w-4 text-neutral-alpha-8" aria-hidden="true" />}
                  </>
                );
              }}
            </ListBoxItem>
          ))}
        </ListBox>
        {footer && <div className="border-t border-neutral-5 p-1">{footer}</div>}
      </Popover>
      {helperText && (
        <Text slot="description" className="text-12 text-neutral-alpha-7">
          {helperText}
        </Text>
      )}
    </ComboBox>
  );
}

AgAutocomplete.displayName = 'AgAutocomplete';
